import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import { Badge } from "@shared/ui/badge";
import { Card } from "@shared/ui/card";
import { getStatusBadgeClass } from "@shared/utils/statusUtils";
import { useMemo, useState } from "react";
import {
  useGetCycleMonthList,
  useGetCycleNameList,
  useGetMeterReaderAssignedRoutes,
  useGetRouteNameList,
} from "../../../../hooks";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { BUTTON_FIELD_CLASSES, FIELD_CLASSES } from "@shared/utils/constants";

const FilterPanel = ({ filterValues, setFilterValues }: { filterValues: any, setFilterValues: any }) => {
  const [searchParams] = useSearchParams();
  const meterReaderId = searchParams.get("view");

  const { data: cycleMonthList } = useGetCycleMonthList(meterReaderId || "");
  const { data: cycleNameList } = useGetCycleNameList(meterReaderId || "");
  const { data: routeNameList } = useGetRouteNameList(meterReaderId || "");

  const cycleMonthOptions = useMemo(() => {
    const list = cycleMonthList?.result || cycleMonthList || [];
    return list.map((item: string) => ({
      label: item,
      value: item,
    }));
  }, [cycleMonthList]);

  const cycleNameOptions = useMemo(() => {
    const list = cycleNameList?.result || cycleNameList || [];
    return list.map((item: string) => ({
      label: item,
      value: item,
    }));
  }, [cycleNameList]);

  const routeNameOptions = useMemo(() => {
    const list = routeNameList?.result || routeNameList || [];
    return list.map((item: string) => ({
      label: item,
      value: item,
    }));
  }, [routeNameList]);

  const filterFields: FormField[] = [
    {
      name: "cycle_month",
      label: "Cycle Month",
      type: "select",
      options: cycleMonthOptions,
      placeholder: "Select Cycle Month",
    },
    {
      name: "cycle",
      label: "Cycle Name",
      type: "select",
      options: cycleNameOptions,
      placeholder: "Select Cycle Name",
    },
    {
      name: "route",
      label: "Route Name filter",
      type: "select",
      options: routeNameOptions,
      placeholder: "Select Route Name",
    },
  ];

  const form = FormService.createForm({
    fields: filterFields,
    mode: "onSubmit",
    defaultValues: filterValues
  });

  const handleApplyFilter = (values: any) => {
    setFilterValues({
      cycle_month: values.cycle_month,
      cycle: values.cycle,
      route: values.route,
    });
  };

  const handleClearFilter = () => {
    form.reset();
    setFilterValues({});
  };

  return (
    <div className="w-full">
      <DynamicForm
        fields={filterFields}
        form={form}
        onSubmit={handleApplyFilter}
        onReset={handleClearFilter}
        gridColumns={3}
        config={{
          classes: BUTTON_FIELD_CLASSES,
          showSubmitButton: true,
          showResetButton: true,
          submitButtonText: "Apply",
          resetButtonText: "Clear",
        }}
      />
    </div>
  );
};

export const RoutesTab = () => {
  const [searchParams] = useSearchParams();
  const id = searchParams.get("view");
  const navigate = useNavigate();
  const remoteUtilityId = getRemoteUtilityId();

  const [currentPage, setCurrentPage] = useState(1);
  const [filterValues, setFilterValues] = useState<Record<string, any>>({});
  const [searchQuery, setSearchQuery] = useState("");
  const pageSize = 10;

  const { data: assignedRoutes } = useGetMeterReaderAssignedRoutes(
    remoteUtilityId,
    id,
    filterValues,
  );

  const data = assignedRoutes?.data || [];

  const filterOptions = useMemo(() => {
    const getOptions = (key: string) => {
      if (!data) return [];
      const values = Array.from(new Set(data.map((item: any) => item[key])));
      return values.map((val: any) => ({ label: val, value: val }));
    };

    return {
      cycle_month: getOptions("cycle_month"),
      cycle: getOptions("cycle"),
      route: getOptions("route"),
    };
  }, [data]);

  // Filter Data
  const filteredData = useMemo(() => {
    if (!data) return [];
    return data.filter((item: any) => {
      // Search Filter
      const matchesSearch = Object.values(item).some((val) =>
        String(val).toLowerCase().includes(searchQuery.toLowerCase()),
      );

      if (!matchesSearch) return false;

      // Dropdown Filters
      if (
        filterValues.cycle_month &&
        item.cycleMonth !== filterValues.cycle_month
      )
        return false;
      if (filterValues.cycle && item.cycle !== filterValues.cycle) return false;
      if (filterValues.route && item.route !== filterValues.route) return false;

      return true;
    });
  }, [filterValues, searchQuery, data]);

  // Pagination Logic
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredData.slice(startIndex, startIndex + pageSize);
  }, [filteredData, currentPage]);

  const columns: TableColumn<any>[] = [
    {
      key: "cycleMonth",
      header: "Cycle Month",
    },
    {
      key: "cycle",
      header: "Cycle",
    },
    {
      key: "route",
      header: "Route",
    },
    {
      key: "meters",
      header: "Meters",
    },
    {
      key: "premises",
      header: "Premises",
    },
    {
      key: "readingsCollected",
      header: "Readings Collected",
    },
    {
      key: "assignedDate",
      header: "Assigned Date",
    },
    {
      key: "dueDate",
      header: "Due Date",
      render: (value: any) => value || "-",
    },
    {
      key: "status",
      header: "Status",
      render: (value) => (
        <Badge className={getStatusBadgeClass(value)}>{value}</Badge>
      ),
    },
  ];

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    setCurrentPage(1);
  };



  return (
    <Card className="h-full border-none shadow-none">
      <div className="mb-4">
        <h2 className="text-lg font-semibold">Assigned Routes</h2>
      </div>
      <GenericTable
        data={paginatedData}
        columns={columns}
        rowKey="route" // Using route as key since id is not present, or maybe combination
        pagination={{
          currentPage,
          totalPages: Math.ceil(filteredData.length / pageSize),
          totalItems: filteredData.length,
          itemsPerPage: pageSize,
          startItem: (currentPage - 1) * pageSize + 1,
          endItem: Math.min(currentPage * pageSize, filteredData.length),
        }}
        onPageChange={handlePageChange}
        searchPlaceholder="Search routes..."
        onSearch={(term) => console.log("Searching:", term)}
        filterPanel={<FilterPanel filterValues={filterValues} setFilterValues={setFilterValues} />}
        emptyMessage="No routes found"
      />
    </Card>
  );
};
